import {
  Placement,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import * as React from 'react';

/*
  Richard comment:
  99% of this is just from floating ui example
  some of it is overkill, but it's easier to just copy and use in our app
*/

export interface PopoverOptions {
  initialOpen?: boolean;
  placement?: Placement;
  modal?: boolean;
  open?: boolean;
  enableArrow?: boolean;
  offsetPx?: number;
  shiftPx?: number;
  onOpenChange?: (open: boolean) => void;
  disableClick?: boolean;
  fixed?: boolean;
}

export function usePopover({
  initialOpen = false,
  placement = 'bottom',
  modal,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  enableArrow,
  offsetPx,
  disableClick = false,
  fixed = false,
}: PopoverOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
  const arrowRef = React.useRef<SVGSVGElement>(null);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;
  const middleware = [
    offset(offsetPx ?? (enableArrow ? 12 : 5)),
    flip({
      crossAxis: placement.includes('-'),
      fallbackAxisSideDirection: 'end',
      padding: 5,
    }),
    shift({ padding: 24 }),
  ];
  if (enableArrow) middleware.push(arrow({ element: arrowRef, padding: 20 }));

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware,
    strategy: fixed ? 'fixed' : undefined,
  });

  const context = data.context;

  const click = useClick(context, { enabled: !disableClick });
  const dismiss = useDismiss(context, { outsidePressEvent: 'click' });
  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      arrowRef,
      ...interactions,
      ...data,
      enableArrow,
      modal,
    }),
    [open, setOpen, interactions, data, modal, enableArrow]
  );
}

type ContextType = ReturnType<typeof usePopover> | null;

export const PopoverContext = React.createContext<ContextType>(null);

export const usePopoverContext = () => {
  const context = React.useContext(PopoverContext);

  if (context == null) {
    throw new Error('Popover components must be wrapped in <VoxelPopover />');
  }

  return context;
};
